import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import DeadlineBanner from "@/components/feature/DeadlineBanner";
import supabase from "@/lib/supabase";
import useClassSchedules, { formatScheduleLabel } from "@/hooks/useClassSchedules";

type ApplyModalProps = {
  open: boolean;
  onClose: () => void;
};

type Status = "idle" | "submitting" | "success" | "error";

const GOALS = [
  "일본 여행 회화",
  "애니·드라마·음악 이해",
  "기초 문법 정리",
  "JLPT 준비 (N5·N4)",
  "일본 유학·취업 준비",
  "취미",
  "기타",
];

const LEVELS = [
  { value: "none", label: "완전 초보 (히라가나 모름)" },
  { value: "hiragana", label: "히라가나만 알아요" },
  { value: "basic", label: "기초 문장은 조금 알아요" },
];

const AGE_GROUPS = ["중학생", "고등학생", "대학생", "직장인", "기타"];

const REFERRALS = ["인스타그램", "지인 추천", "인터넷 검색", "블로그·카페", "유튜브", "기타"];

export default function ApplyModal({ open, onClose }: ApplyModalProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string>("");
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<string>("");
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>("");
  const [selectedReferral, setSelectedReferral] = useState<string>("");
  const [cohortName, setCohortName] = useState<string>("");
  const [agreed, setAgreed] = useState(false);

  // Active cohort ID for class schedules
  const [activeCohortId, setActiveCohortId] = useState<number | null>(null);

  // Dynamic class schedules
  const { schedules: classSchedules } = useClassSchedules(activeCohortId);

  // Build dynamic schedule options
  const scheduleOptions = [
    ...classSchedules.map((s) => formatScheduleLabel(s)),
    "상관없음",
  ];

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";

    // Fetch active cohort id and name
    let cancelled = false;
    const fetchCohort = async () => {
      try {
        const raw = localStorage.getItem("active_cohort_id");
        const cohortId = raw ? Number(raw) : null;
        if (!cohortId) return;
        if (!cancelled) setActiveCohortId(cohortId);
        const { data } = await supabase
          .from("cohorts")
          .select("name")
          .eq("id", cohortId)
          .maybeSingle();
        if (!cancelled && data) {
          setCohortName(data.name);
        }
      } catch {
        // silently fail — cohort badge is optional
      }
    };
    fetchCohort();

    return () => {
      document.body.style.overflow = "";
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStatus("idle");
      setError("");
      setSelectedGoals([]);
      setSelectedSchedule("");
      setSelectedAgeGroup("");
      setSelectedReferral("");
      setCohortName("");
      setAgreed(false);
    }
  }, [open]);

  const toggleGoal = (goal: string) => {
    setSelectedGoals((prev) =>
      prev.includes(goal) ? prev.filter((g) => g !== goal) : [...prev, goal]
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const form = event.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const hp = (formData.get("website_alt") || "").toString().trim();
    if (hp) {
      setStatus("success");
      form.reset();
      setSelectedGoals([]);
      setSelectedSchedule("");
      setSelectedAgeGroup("");
      setSelectedReferral("");
      return;
    }
    formData.delete("website_alt");

    // Validate required pill fields
    if (!agreed) {
      setError("이용약관 및 개인정보 수집에 동의해 주세요.");
      setStatus("error");
      return;
    }
    if (!selectedSchedule) {
      setError("희망 시간을 선택해 주세요.");
      setStatus("error");
      return;
    }
    if (!selectedAgeGroup) {
      setError("연령을 선택해 주세요.");
      setStatus("error");
      return;
    }
    if (selectedGoals.length === 0) {
      setError("수강 목표를 하나 이상 선택해 주세요.");
      setStatus("error");
      return;
    }

    // Textarea length limit
    const message = (formData.get("message") || "").toString();
    if (message.length > 500) {
      setError("문의 내용은 500자 이내로 작성해 주세요.");
      setStatus("error");
      return;
    }

    // Add multi-select goals as comma joined
    formData.delete("goals");
    if (selectedGoals.length > 0) {
      formData.append("goals", selectedGoals.join(", "));
    }

    // Add schedule
    if (selectedSchedule) {
      formData.append("schedule", selectedSchedule);
    }

    // Add age group
    if (selectedAgeGroup) {
      formData.append("age_group", selectedAgeGroup);
    }

    // Add referral
    formData.delete("referral");
    if (selectedReferral) {
      formData.append("referral", selectedReferral);
    }

    setStatus("submitting");
    setError("");

    try {
      // Get active cohort id from localStorage
      let cohortId: number | null = null;
      try {
        const raw = localStorage.getItem("active_cohort_id");
        cohortId = raw ? Number(raw) : null;
      } catch {
        // storage unavailable
      }

      // Store in Supabase with cohort tracking
      if (cohortId) {
        const appData: Record<string, string> = {};
        formData.forEach((value, key) => {
          appData[key] = value.toString();
        });

        await supabase.from("applications").insert({
          cohort_id: cohortId,
          name: appData.name || "",
          phone: appData.phone || "",
          email: appData.email || "",
          level: appData.level || "",
          schedule: selectedSchedule,
          age_group: selectedAgeGroup,
          goals: selectedGoals.join(", "),
          referral: selectedReferral || "",
          message: appData.message || "",
        });
      }

      // Submit to form API (existing workflow)
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value.toString());
      });

      const response = await fetch("https://readdy.ai/api/form/d9h07h4oq4dpg2lrmtsg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.code || responseText;

      const looksLikeSpam =
        typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !looksLikeSpam) {
        form.reset();
        setSelectedGoals([]);
        setSelectedSchedule("");
        setSelectedAgeGroup("");
        setSelectedReferral("");
        onClose();
        navigate("/thank-you");
      } else {
        setStatus("error");
        setError(
          typeof serverMessage === "string" && serverMessage
            ? serverMessage
            : "신청 접수 중 문제가 발생했어요. 잠시 후 다시 시도해 주세요."
        );
      }
    } catch (err) {
      setStatus("error");
      setError("네트워크 오류가 발생했어요. 잠시 후 다시 시도해 주세요.");
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 md:p-4 bg-foreground-950/60 backdrop-blur-sm">
      <div className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto bg-background-50 rounded-xl md:rounded-2xl border border-background-200">
        <button
          className="absolute top-3 right-3 md:top-4 md:right-4 w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-background-200 text-foreground-800 cursor-pointer"
          onClick={onClose}
          aria-label="닫기"
        >
          <i className="ri-close-line text-base md:text-lg"></i>
        </button>

        <div className="p-4 md:p-8 pb-3 md:pb-6 border-b border-background-200">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-block text-[10px] md:text-xs font-semibold tracking-widest text-primary-600 uppercase">
              Apply
            </span>
            {cohortName && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-[10px] md:text-xs font-bold">
                <i className="ri-megaphone-line text-[10px]"></i>
                {cohortName} 모집
              </span>
            )}
          </div>
          <h3 className="mt-1.5 md:mt-2 font-heading text-lg md:text-2xl font-bold text-foreground-950">
            왕초보 일본어 8주 실전 기초반 신청
          </h3>
          <p className="mt-1.5 md:mt-2 text-xs md:text-sm text-foreground-700">
            최대 5명 소수정예로 진행되며, 신청서 접수 후 개별 안내를 드립니다.
          </p>
        </div>

        <div className="mx-4 md:mx-8 mt-3 md:mt-4">
          <DeadlineBanner />
        </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            id="apply-form"
            className="p-4 md:p-8 pt-4 md:pt-6 space-y-4 md:space-y-5"
          >
            {/* honeypot */}
            <div className="hp-field-wrap" aria-hidden="true">
              <label>
                Website
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                  이름 <span className="ml-0.5 text-primary-600 font-bold">*</span>
                </label>
                <input
                  name="name"
                  required
                  type="text"
                  placeholder="홍길동"
                  className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                  연락처 <span className="ml-0.5 text-primary-600 font-bold">*</span>
                </label>
                <input
                  name="phone"
                  required
                  type="tel"
                  placeholder="010-0000-0000"
                  className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                이메일 <span className="ml-0.5 text-primary-600 font-bold">*</span>
              </label>
              <input
                name="email"
                required
                type="email"
                placeholder="you@example.com"
                className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                현재 일본어 수준 <span className="ml-0.5 text-primary-600 font-bold">*</span>
              </label>
              <select
                name="level"
                required
                defaultValue=""
                className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900 cursor-pointer"
              >
                <option value="" disabled>
                  선택해 주세요
                </option>
                {LEVELS.map((l) => (
                  <option key={l.value} value={l.label}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-2">
                희망 시간 <span className="ml-0.5 text-primary-600 font-bold">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {scheduleOptions.map((s) => {
                  const active = selectedSchedule === s;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setSelectedSchedule(active ? "" : s)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap cursor-pointer ${
                        active
                          ? "bg-primary-500 border-primary-500 text-background-50"
                          : "bg-background-50 border-background-300 text-foreground-800 hover:border-primary-300"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-2">
                연령 <span className="ml-0.5 text-primary-600 font-bold">*</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {AGE_GROUPS.map((a) => {
                  const active = selectedAgeGroup === a;
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setSelectedAgeGroup(active ? "" : a)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap cursor-pointer ${
                        active
                          ? "bg-primary-500 border-primary-500 text-background-50"
                          : "bg-background-50 border-background-300 text-foreground-800 hover:border-primary-300"
                      }`}
                    >
                      {a}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-2">
                수강 목표 <span className="ml-0.5 text-primary-600 font-bold">*</span> <span className="font-normal text-foreground-500">(복수 선택 가능)</span>
              </label>
              <div className="flex flex-wrap gap-2">
                {GOALS.map((g) => {
                  const active = selectedGoals.includes(g);
                  return (
                    <button
                      key={g}
                      type="button"
                      onClick={() => toggleGoal(g)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap cursor-pointer ${
                        active
                          ? "bg-primary-500 border-primary-500 text-background-50"
                          : "bg-background-50 border-background-300 text-foreground-800 hover:border-primary-300"
                      }`}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-2">
                어떻게 알게 되셨나요? (선택)
              </label>
              <div className="flex flex-wrap gap-2">
                {REFERRALS.map((r) => {
                  const active = selectedReferral === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setSelectedReferral(active ? "" : r)}
                      className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors whitespace-nowrap cursor-pointer ${
                        active
                          ? "bg-primary-500 border-primary-500 text-background-50"
                          : "bg-background-50 border-background-300 text-foreground-800 hover:border-primary-300"
                      }`}
                    >
                      {r}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-700 mb-1.5">
                문의 내용 (선택, 최대 500자)
              </label>
              <textarea
                name="message"
                rows={4}
                maxLength={500}
                placeholder="궁금하신 점이나 강사에게 미리 알리고 싶은 내용을 적어 주세요."
                className="w-full px-3.5 py-2.5 rounded-md bg-background-50 border border-background-300 focus:border-primary-400 focus:ring-2 focus:ring-primary-100 outline-none text-sm text-foreground-900 resize-none"
              ></textarea>
            </div>

            <div className="flex items-start gap-2.5">
              <input
                id="agree"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-primary-500 cursor-pointer"
              />
              <label htmlFor="agree" className="text-sm text-foreground-700 leading-relaxed cursor-pointer">
                <span className="text-primary-600 font-bold">*</span>{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  이용약관
                </a>
                {" "}및{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                  개인정보 처리방침
                </a>
                에 동의합니다.
              </label>
            </div>

            {status === "error" && (
              <div className="p-3 rounded-md bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                {error}
              </div>
            )}

            <div className="pt-1 md:pt-2 flex flex-col sm:flex-row gap-2 md:gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 md:px-5 py-2.5 md:py-3 rounded-md border border-background-300 text-foreground-800 hover:bg-background-100 text-xs md:text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={status === "submitting"}
                className="flex-1 inline-flex items-center justify-center gap-1.5 md:gap-2 px-5 md:px-6 py-2.5 md:py-3 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-xs md:text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70"
              >
                {status === "submitting" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i> 접수 중...
                  </>
                ) : (
                  <>
                    신청서 제출하기
                    <i className="ri-arrow-right-line"></i>
                  </>
                )}
              </button>
            </div>
          </form>
      </div>
    </div>
  );
}